import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import axios from '../utils/api'
import { Gift, GitPullRequest, Award, TrendingUp, GitFork, Star, ExternalLink } from 'lucide-react'
import { getRefreshedAccessToken, getGitHubRepos } from '../utils/methods'

export default function UserDashboard() {
  const navigate = useNavigate()
  const [dashboardData, setDashboardData] = useState(null)
  const [userRepos, setUserRepos] = useState([])
  const [allUserRepos, setAllUserRepos] = useState([])
  const [orgRepos, setOrgRepos] = useState([])
  const [submissions, setSubmissions] = useState([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [submittingRepo, setSubmittingRepo] = useState(null)
  const [userRepoSearch, setUserRepoSearch] = useState('')
  const [showAllUserRepos, setShowAllUserRepos] = useState(false)

  useEffect(() => {
    let cancelled = false

    const fetchAllData = async () => {
      try {
        const auth = JSON.parse(localStorage.getItem('auth'))
        if (!auth?.token) {
          window.location.href = '/'
          return
        }

        const headers = { Authorization: `Bearer ${auth.token}` }

        // Fetch all data in parallel
        const [dashboardRes, reposRes, approvedReposRes, submissionsRes] = await Promise.allSettled([
          axios.get('/user/dashboard', { headers }),
          getGitHubRepos({ sort: 'updated', affiliation: 'owner' }),
          axios.get('/user/approved-repos', { headers }),
          axios.get('/user/submissions', { headers })
        ])

        // Check if component unmounted
        if (cancelled) return

        // Process dashboard
        if (dashboardRes.status === 'fulfilled') {
          setDashboardData(dashboardRes.value.data)
        } else if (dashboardRes.reason?.response?.status === 401) {
          try {
            const authData = JSON.parse(localStorage.getItem('auth'))
            if (authData?.refreshToken) {
              const refreshRes = await getRefreshedAccessToken(authData.refreshToken)
              localStorage.setItem('auth', JSON.stringify(refreshRes.data))
              if (!cancelled) {
                // Retry fetch with new token
                return fetchAllData()
              }
            }
          } catch {
            localStorage.removeItem('auth')
            window.location.href = '/'
            return
          }
        }

        // Process repos
        if (reposRes.status === 'fulfilled') {
          const repos = reposRes.value.data || []
          if (!cancelled) {
            setAllUserRepos(repos)
            setUserRepos(repos.slice(0, 5))
          }
        } else {
          console.error('Error fetching repos:', reposRes.reason)
          if (!cancelled) {
            setAllUserRepos([])
            setUserRepos([])
          }
        }

        // Process approved repos
        if (approvedReposRes.status === 'fulfilled' && !cancelled) {
          setOrgRepos(approvedReposRes.value.data.repos || [])
        }

        // Process submissions
        if (submissionsRes.status === 'fulfilled' && !cancelled) {
          setSubmissions(submissionsRes.value.data.submissions || [])
        }

      } catch (error) {
        if (cancelled) return
        console.error('Error fetching data:', error)
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchAllData()

    return () => {
      cancelled = true
    }
  }, [])

  const submitRepository = async (repo) => {
    if (submittingRepo) return
    
    setSubmittingRepo(repo.id)
    try {
      const auth = JSON.parse(localStorage.getItem('auth'))
      
      await axios.post('/user/submit-repo', {
        repoName: repo.name,
        repoUrl: repo.html_url,
        description: repo.description,
        language: repo.language,
        isPrivate: repo.private
      }, {
        headers: { Authorization: `Bearer ${auth.token}` }
      })

      alert('Repository submitted successfully! It will be reviewed by the admin.')
      // Refresh submissions only
      const submissionsRes = await axios.get('/user/submissions', {
        headers: { Authorization: `Bearer ${auth.token}` }
      })
      setSubmissions(submissionsRes.data.submissions || [])
    } catch (error) {
      console.error('Error submitting repository:', error)
      alert('Failed to submit repository: ' + (error.response?.data?.message || error.message))
    } finally {
      setSubmittingRepo(null)
    }
  }

  const isRepoSubmitted = (repoUrl) => {
    return submissions.some(s => s.repoUrl === repoUrl)
  }

  const getSubmissionStatus = (repoUrl) => {
    const submission = submissions.find(s => s.repoUrl === repoUrl)
    return submission?.status
  }

  const handleLoadMore = () => {
    setShowAllUserRepos(true)
    setUserRepos(allUserRepos)
  }

  const handleSearchChange = (e) => {
    const searchTerm = e.target.value.toLowerCase()
    setUserRepoSearch(searchTerm)
    
    if (searchTerm === '') {
      setUserRepos(showAllUserRepos ? allUserRepos : allUserRepos.slice(0, 5))
    } else {
      const filtered = allUserRepos.filter(repo => 
        repo.name.toLowerCase().includes(searchTerm) ||
        (repo.description && repo.description.toLowerCase().includes(searchTerm))
      )
      setUserRepos(filtered)
    }
  }

  const syncContributions = async () => {
    setSyncing(true)
    try {
      const auth = JSON.parse(localStorage.getItem('auth'))
      await axios.post('/user/sync', 
        { syncType: 'full' }, // Full sync includes contribution tracking from approved repos
        {
          headers: { Authorization: `Bearer ${auth.token}` }
        }
      )
      alert('Contributions synced successfully!')
      // Refresh dashboard data
      const dashboardRes = await axios.get('/user/dashboard', {
        headers: { Authorization: `Bearer ${auth.token}` }
      })
      setDashboardData(dashboardRes.data)
    } catch (error) {
      console.error('Error syncing contributions:', error)
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to sync contributions'
      const errorHint = error.response?.data?.hint
      
      if (error.response?.status === 401 && errorMessage.includes('expired')) {
        alert(`${errorMessage}\n\n${errorHint || 'Please log out and log back in with GitHub to refresh your credentials.'}`)
      } else {
        alert(errorMessage)
      }
    } finally {
      setSyncing(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    )
  }

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-red-500">Failed to load dashboard</div>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">
            Welcome back, {dashboardData.user.name || dashboardData.user.username}!
          </p>
        </div>
        <Button onClick={syncContributions} disabled={syncing} className="w-full sm:w-auto">
          {syncing ? 'Syncing...' : 'Sync Contributions'}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Points</CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData.user.totalPoints}</div>
            <p className="text-xs text-muted-foreground">
              Available for redemption
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Contributions</CardTitle>
            <GitPullRequest className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData.totalContributions}</div>
            <p className="text-xs text-muted-foreground">
              Across all repositories
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Development</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {dashboardData.contributionsByType?.find(c => c.type === 'development')?.count || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              PRs and commits
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Quick Actions</CardTitle>
            <Gift className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full" asChild>
              <a href="/store">Visit Store</a>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Contribution Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Contributions by Type</CardTitle>
          <CardDescription>Your contribution distribution</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {dashboardData.contributionsByType?.map((item) => (
              <div key={item.type} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-primary" />
                  <span className="capitalize">{item.type.replace('_', ' ')}</span>
                </div>
                <span className="font-semibold">{item.count}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Contributions */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Contributions</CardTitle>
          <CardDescription>Your latest activity</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {dashboardData.recentContributions?.map((contribution) => (
              <div key={contribution.id} className="flex items-center justify-between border-b pb-4 last:border-0">
                <div>
                  <p className="font-medium">{contribution.title}</p>
                  <p className="text-sm text-muted-foreground">
                    {contribution.repo?.name} • {new Date(contribution.contributedAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-semibold text-primary">
                    +{contribution.pointsEarned} pts
                  </span>
                  {contribution.githubUrl && (
                    <Button variant="ghost" size="sm" asChild>
                      <a href={contribution.githubUrl} target="_blank" rel="noopener noreferrer">
                        View
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Repositories Section */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* User Repositories */}
        <Card>
          <CardHeader>
            <CardTitle>Your Repositories</CardTitle>
            <CardDescription>Your personal GitHub repositories (showing {userRepos.length} of {allUserRepos.length})</CardDescription>
            {/* Search Input */}
            <div className="mt-3">
              <input
                type="text"
                placeholder="Search repositories..."
                value={userRepoSearch}
                onChange={handleSearchChange}
                className="w-full px-3 py-2 border border-input rounded-md text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {userRepos.length === 0 ? (
                <p className="text-sm text-muted-foreground">No repositories found</p>
              ) : (
                userRepos.map((repo) => {
                  const submissionStatus = getSubmissionStatus(repo.html_url);
                  const isSubmitted = isRepoSubmitted(repo.html_url);
                  
                  return (
                    <div key={repo.id} className="border rounded-lg p-4 hover:bg-accent transition-colors">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold truncate">{repo.name}</h3>
                            {repo.private && (
                              <Badge variant="secondary" className="text-xs">Private</Badge>
                            )}
                            {isSubmitted && submissionStatus && (
                              <Badge 
                                variant={
                                  submissionStatus === 'approved' ? 'default' : 
                                  submissionStatus === 'rejected' ? 'destructive' : 
                                  'secondary'
                                }
                                className="text-xs"
                              >
                                {submissionStatus.charAt(0).toUpperCase() + submissionStatus.slice(1)}
                              </Badge>
                            )}
                          </div>
                          {repo.description && (
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{repo.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
                            {repo.language && (
                              <span className="flex items-center gap-1">
                                <div className="h-3 w-3 rounded-full bg-blue-500" />
                                {repo.language}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Star className="h-3 w-3" />
                              {repo.stargazers_count}
                            </span>
                            <span className="flex items-center gap-1">
                              <GitFork className="h-3 w-3" />
                              {repo.forks_count}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button variant="ghost" size="sm" asChild>
                            <a href={repo.html_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </Button>
                          {!isSubmitted && (
                            <Button 
                              size="sm" 
                              onClick={() => submitRepository(repo)}
                              disabled={submittingRepo === repo.html_url}
                              className="whitespace-nowrap"
                            >
                              {submittingRepo === repo.html_url ? 'Submitting...' : 'Submit to FOSSIT'}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            {/* Load More Button */}
            {!showAllUserRepos && allUserRepos.length > 5 && userRepoSearch === '' && (
              <div className="mt-4 text-center">
                <Button 
                  variant="outline" 
                  onClick={handleLoadMore}
                  className="w-full"
                >
                  Load More ({allUserRepos.length - 5} more)
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Organization Repositories */}
        <Card>
          <CardHeader>
            <CardTitle>Open for Contributions</CardTitle>
            <CardDescription>Repositories from {process.env.REACT_APP_GITHUB_ORG || 'fossit-org'}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {orgRepos.length === 0 ? (
                <p className="text-sm text-muted-foreground">No repositories found</p>
              ) : (
                orgRepos.map((repo) => {
                  // Extract owner and repo name from githubUrl
                  const urlParts = repo.githubUrl?.split('/') || []
                  const owner = urlParts[urlParts.length - 2] || 'fossit-org'
                  const repoName = urlParts[urlParts.length - 1] || repo.name
                  
                  return (
                    <div 
                      key={repo.id} 
                      className="border rounded-lg p-4 hover:bg-accent transition-colors cursor-pointer"
                      onClick={() => navigate(`/repo/${owner}/${repoName}`)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold">{repo.name}</h3>
                            {repo.isPrivate && (
                              <Badge variant="secondary" className="text-xs">Private</Badge>
                            )}
                            {repo.sponsor && (
                              <Badge variant="outline" className="text-xs">
                                Sponsored by {repo.sponsor.name}
                              </Badge>
                            )}
                          </div>
                          {repo.description && (
                            <p className="text-sm text-muted-foreground mt-1">{repo.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                            {repo.language && (
                              <span className="flex items-center gap-1">
                                <div className="h-3 w-3 rounded-full bg-blue-500" />
                                {repo.language}
                              </span>
                            )}
                            {repo.pointsMultiplier && repo.pointsMultiplier !== 1 && (
                              <Badge variant="default" className="text-xs">
                                {repo.pointsMultiplier}x points
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          asChild
                          onClick={(e) => e.stopPropagation()}
                        >
                          <a href={repo.githubUrl} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </Button>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
